import {
  GLOBAL_CONFIG,
  PROTOCOL_CONFIG,
  SILO_V1_TOKEN_ADDRESSES,
  TOKEN_ADDRESS,
} from "@silo/config";
import {
  Address,
  getChainKeyFromProtocol,
  makeUsd,
  ProdChainKey,
  ServerError,
} from "@silo/types";
import { Effect, Fiber, pipe, Record } from "effect";
import { isNotNullable } from "effect/Predicate";
import { MerklClient } from "../../merkl-client";
import { RewardToken } from "../../types";
import { KvClient } from "../../kv-client";
import { CmcClient } from "../../cmc-client";
import { getMerklHistoricalRewardTokenArray } from "../../kv-client/merkl-user-rewards-info";
import { getCoinGeckoPriceUsd } from "../../coin-gecko/get-coin-gecko-price-usd";
import { ContractsClientError } from "../../contracts-client/types";
import { KvClientError, KvService, KvTokenPrice } from "../../kv-client/types";
import { Schema } from "@effect/schema";
import { getTokenPriceKey } from "../../kv-client/keys";
import { TTL } from "../../kv-client/constants";

export type UpdateRewardTokenPrice = ReturnType<typeof updateRewardTokenPrice>;
export type UpdateRewardTokenPriceSuccess =
  Effect.Effect.Success<UpdateRewardTokenPrice>;
export type UpdateRewardTokenPriceError =
  Effect.Effect.Error<UpdateRewardTokenPrice>;
export type UpdateRewardTokenPriceContext =
  Effect.Effect.Context<UpdateRewardTokenPrice>;

export function updateRewardTokenPrice() {
  return Effect.gen(function* () {
    const setSiloPriceFiber = yield* pipe(setSiloPrice(), Effect.fork);

    /**
     * Get all native and merkl reward tokens
     */
    const rewardTokens = yield* getAllRewardTokens();

    yield* Effect.log(`Count of all reward tokens: ${rewardTokens.length}`);

    /**
     * Filter out SILO token
     */

    /**
     * Determine which tokens do not currently have coin market cap metadata in Redis
     */
    const unpopulatedTokens = yield* KvClient.getUnpopulatedCmcTokens(
      rewardTokens
    );

    yield* Effect.log(
      `Count of unpopulated tokens: ${unpopulatedTokens.length}`
    );

    /**
     * For any tokens that don't have metadata, fetch and save it in Redis
     */
    yield* unpopulatedTokens.length > 0
      ? pipe(
          CmcClient.getTokenMeta(unpopulatedTokens),
          Effect.andThen(({ identified }) => KvClient.setCmcMeta(identified))
        )
      : Effect.void;

    /**
     * Fetch coin market cap metadata from Redis for all tokens
     */
    const { satisfying: cmcTokens, excluded } =
      yield* KvClient.getCmcTokenArray(rewardTokens);

    yield* Effect.log(
      `Count of tokens missing CMC metadata: ${excluded.length}`
    );

    /**
     * Fetch price for all tokens from coin market cap API
     */
    const cmcTokensWithPrice = yield* CmcClient.getTokenPrice(cmcTokens);

    /**
     * Save prices to Redis
     */
    yield* KvClient.setCmcPrice(cmcTokensWithPrice);
    yield* Fiber.join(setSiloPriceFiber);

    yield* Effect.log(
      `Count of token prices updated: ${cmcTokensWithPrice.length}`
    );
  });
}

export type GetAllRewardTokens = ReturnType<typeof getAllRewardTokens>;
export type GetAllRewardTokensSuccess =
  Effect.Effect.Success<GetAllRewardTokens>;
export type GetAllRewardTokensError = Effect.Effect.Error<GetAllRewardTokens>;
export type GetAllRewardTokensContext =
  Effect.Effect.Context<GetAllRewardTokens>;

export function getAllRewardTokens() {
  return Effect.gen(function* () {
    const rewardTokenMap: Map<Address, RewardToken> = new Map();

    // add all native reward tokens
    for (const [protocolKey, { nativeRewards }] of Record.toEntries(
      PROTOCOL_CONFIG
    )) {
      if (isNotNullable(nativeRewards)) {
        const { tokenAddress, symbol } = nativeRewards;
        const chainKey = getChainKeyFromProtocol(protocolKey);

        rewardTokenMap.set(tokenAddress, { tokenAddress, symbol, chainKey });
      }
    }

    // add merkl historical campaign tokens
    const historicalRewardTokens = yield* getMerklHistoricalRewardTokenArray();

    for (const token of historicalRewardTokens) {
      rewardTokenMap.set(token.tokenAddress, token);
    }

    // add all merkl campaign tokens
    const merklCampaignMetaArray = yield* MerklClient.getCampaignMetaArray({
      activeOnly: false,
    });

    for (const {
      chainKey,
      pricingTokenAddress,
      pricingTokenSymbol,
    } of merklCampaignMetaArray) {
      rewardTokenMap.set(pricingTokenAddress, {
        chainKey,
        tokenAddress: pricingTokenAddress,
        symbol: pricingTokenSymbol,
      });
    }

    // add revenue tokens
    const {
      rewardTokenAddress,
      rewardTokenSymbol,
      targetTokenAddress,
      targetTokenSymbol,
      protocolKey,
    } = GLOBAL_CONFIG.revenue;

    const chainKey = getChainKeyFromProtocol(protocolKey);

    rewardTokenMap.set(rewardTokenAddress, {
      tokenAddress: rewardTokenAddress,
      symbol: rewardTokenSymbol,
      chainKey,
    });

    rewardTokenMap.set(targetTokenAddress, {
      tokenAddress: targetTokenAddress,
      symbol: targetTokenSymbol,
      chainKey,
    });

    /**
     * Remove SILO v1 tokens from reward token map. CMC does not return a price
     * for SILOv1, so we do not use the CMC flow.
     */
    for (const tokenAddress of SILO_V1_TOKEN_ADDRESSES) {
      rewardTokenMap.delete(tokenAddress);
    }

    const rewardTokens = [...rewardTokenMap.values()];

    return rewardTokens;
  });
}

/**
 * If a new chain is added, check on this logic
 */
export const SILO_PRICE_GUARD: Record<ProdChainKey, true> = {
  arbitrum: true,
  optimism: true,
  base: true,
  ethereum: true,
};

function setSiloPrice() {
  return Effect.gen(function* () {
    const { value } = yield* Effect.tryPromise({
      try: () => getCoinGeckoPriceUsd("silo-finance"),
      catch: (cause) => new ServerError({ cause }),
    });

    const encodedPriceUsd = yield* pipe(
      makeUsd(value),
      Schema.encode(KvTokenPrice)
    );

    const kv = yield* KvService;

    yield* Effect.tryPromise({
      try: async () => {
        const pipeline = kv.pipeline();

        const arbitrumKey = getTokenPriceKey(
          "arbitrum",
          TOKEN_ADDRESS.arbitrum.SILO
        );

        const optimismKey = getTokenPriceKey(
          "optimism",
          TOKEN_ADDRESS.optimism.SILO
        );

        const baseKey = getTokenPriceKey("base", TOKEN_ADDRESS.base.SILO);

        const ethereumKey = getTokenPriceKey(
          "ethereum",
          TOKEN_ADDRESS.ethereum.SILO
        );

        pipeline.set(arbitrumKey, encodedPriceUsd, { ex: TTL.ONE_DAY });
        pipeline.set(optimismKey, encodedPriceUsd, { ex: TTL.ONE_DAY });
        pipeline.set(baseKey, encodedPriceUsd, { ex: TTL.ONE_DAY });
        pipeline.set(ethereumKey, encodedPriceUsd, { ex: TTL.ONE_DAY });

        return pipeline.exec();
      },
      catch: (cause) => new KvClientError({ cause }),
    });

    yield* Effect.log(`SILOv1 price updated`);
  });
}
